package com.yendo.mx.ui.components

import androidx.compose.animation.core.*
import androidx.compose.foundation.background
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp

/**
 * 🚀 GRADIENTE ANIMADO DE SHIMMER SKELETON (UBER EATS / RAPPI / AIRBNB PATTERN)
 */
@Composable
fun rememberShimmerBrush(isDark: Boolean = isSystemInDarkTheme()): Brush {
    val shimmerColors = if (isDark) {
        listOf(
            Color(0xFF242424),
            Color(0xFF383838),
            Color(0xFF242424)
        )
    } else {
        listOf(
            Color(0xFFECECEC),
            Color(0xFFF5F5F5),
            Color(0xFFECECEC)
        )
    }

    val transition = rememberInfiniteTransition(label = "ShimmerTransition")
    val translateAnim by transition.animateFloat(
        initialValue = 0f,
        targetValue = 1200f,
        animationSpec = infiniteRepeatable(
            animation = tween(
                durationMillis = 1000,
                easing = FastOutSlowInEasing
            ),
            repeatMode = RepeatMode.Restart
        ),
        label = "ShimmerTranslate"
    )

    return Brush.linearGradient(
        colors = shimmerColors,
        start = Offset(translateAnim - 300f, translateAnim - 300f),
        end = Offset(translateAnim, translateAnim)
    )
}

/**
 * 🚀 FANTASMA ESQUELETO PARA TARJETAS DE RESTAURANTE / COMERCIO
 */
@Composable
fun RestauranteCardShimmerSkeleton(
    isDark: Boolean = isSystemInDarkTheme(),
    modifier: Modifier = Modifier
) {
    val shimmerBrush = rememberShimmerBrush(isDark)
    val colorBgCard = if (isDark) Color(0xFF1E1E1E) else Color.White

    Card(
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(20.dp),
        colors = CardDefaults.cardColors(containerColor = colorBgCard),
        elevation = CardDefaults.cardElevation(defaultElevation = if (isDark) 0.dp else 2.dp)
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            // PORTADA
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(130.dp)
                    .background(shimmerBrush)
            )

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(12.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // LOGO CIRCULAR
                Box(
                    modifier = Modifier
                        .size(46.dp)
                        .clip(CircleShape)
                        .background(shimmerBrush)
                )

                Spacer(Modifier.width(12.dp))

                Column(modifier = Modifier.weight(1f)) {
                    // LÍNEA TITULO
                    Box(
                        modifier = Modifier
                            .fillMaxWidth(0.6f)
                            .height(16.dp)
                            .clip(RoundedCornerShape(4.dp))
                            .background(shimmerBrush)
                    )

                    Spacer(Modifier.height(8.dp))

                    // LÍNEA DESCRIPCIÓN Y TIEMPOS
                    Box(
                        modifier = Modifier
                            .fillMaxWidth(0.4f)
                            .height(12.dp)
                            .clip(RoundedCornerShape(4.dp))
                            .background(shimmerBrush)
                    )
                }
            }
        }
    }
}

/**
 * 🚀 FANTASMA ESQUELETO PARA SLIDERS Y BANNERS
 */
@Composable
fun BannerShimmerSkeleton(
    isDark: Boolean = isSystemInDarkTheme(),
    modifier: Modifier = Modifier
) {
    val shimmerBrush = rememberShimmerBrush(isDark)

    Box(
        modifier = modifier
            .fillMaxWidth()
            .height(140.dp)
            .padding(horizontal = 16.dp, vertical = 8.dp)
            .clip(RoundedCornerShape(20.dp))
            .background(shimmerBrush)
    )
}

/**
 * 🚀 FANTASMA ESQUELETO PARA BARRA DE CHIPS DE CATEGORÍAS
 */
@Composable
fun CategoriasChipsShimmerSkeleton(
    isDark: Boolean = isSystemInDarkTheme(),
    modifier: Modifier = Modifier
) {
    val shimmerBrush = rememberShimmerBrush(isDark)

    Row(
        modifier = modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 8.dp),
        horizontalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        repeat(5) {
            Box(
                modifier = Modifier
                    .width(90.dp)
                    .height(36.dp)
                    .clip(RoundedCornerShape(20.dp))
                    .background(shimmerBrush)
            )
        }
    }
}
